import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import OmTextField from '../components/ui/OmTextField';
import OmButton from '../components/ui/OmButton';
import OMLogo from '../assets/OM-logo.png';
import apiClient from '../utils/apiClient';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [token, setToken] = useState('');
  const navigate = useNavigate();
  const toast = useToast();

  // Extract token from URL query parameter - don't redirect immediately
  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
      console.log('Token extracted from URL:', tokenFromUrl);
    }
  }, [searchParams]);

  // Only show error after attempting to render, not immediately
  useEffect(() => {
    if (!searchParams.get('token')) {
      // Delay the error to allow component to mount first
      const timer = setTimeout(() => {
        toast.showError('Invalid reset link. Please request a new password reset.');
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [searchParams, toast]);

  const handleChange = (field, e) => {
    setFormData((prevState) => ({
      ...prevState,
      [field]: e.target.value
    }));
    
    // Clear error when user types
    if (errors[field]) {
      setErrors((prevState) => ({
        ...prevState,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // New password validation
    if (!formData.newPassword.trim()) {
      newErrors.newPassword = 'New password is required';
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters long';
    } else {
      // Password strength validation
      const hasUpperCase = /[A-Z]/.test(formData.newPassword);
      const hasLowerCase = /[a-z]/.test(formData.newPassword);
      const hasNumbers = /\d/.test(formData.newPassword);
      const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(formData.newPassword);
      
      if (!hasUpperCase || !hasLowerCase || !hasNumbers || !hasSpecialChar) {
        newErrors.newPassword = 'Password must contain uppercase, lowercase, numbers, and special characters';
      }
    }

    // Confirm password validation
    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = 'Please confirm your new password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!token) {
      toast.showError('Invalid reset link. Please request a new password reset.');
      return;
    }
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    
    try {
      console.log('Resetting password with token...');
      const response = await apiClient.post('/auth/reset-password', {
        token,
        newPassword: formData.newPassword
      });
      
      console.log('Password reset response:', response);
      
      toast.showSuccess(response.data.data?.message || 'Password reset successfully. You can now log in with your new password.');
      
      // Navigate to login after successful reset
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 2000);
      
    } catch (error) {
      console.error('Password reset error:', error);
      
      let errorMessage = 'Failed to reset password. Please try again.';
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.showError(errorMessage);
      
      // If token is invalid/expired, allow user to request new reset
      if (error.response?.status === 401) {
        setTimeout(() => {
          navigate('/forgot-password', { replace: true });
        }, 3000);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Don't render if no token - but also don't redirect immediately
  if (!token && searchParams.get('token') === null) {
    return (
      <div className="min-h-screen bg-silver-mist flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex flex-col items-center space-y-6">
            <div className="flex-shrink-0">
              <img 
                src={OMLogo} 
                alt="Old Mutual Logo" 
                className="h-16 w-auto"
              />
            </div>
            <div className="text-center space-y-4">
              <h1 className="text-2xl md:text-3xl font-display font-bold text-deep-jungle tracking-tight">
                Invalid Reset Link
              </h1>
              <p className="text-steel text-sm font-medium">
                This password reset link is invalid or has expired.
              </p>
              <div className="pt-4">
                <button
                  onClick={() => navigate('/forgot-password')}
                  className="text-fresh-leaf hover:text-forest-fern font-semibold"
                >
                  Request a new password reset
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-silver-mist flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* Logo and Header Section */}
        <div className="flex flex-col items-center space-y-6">
          <div className="flex-shrink-0">
            <img 
              src={OMLogo} 
              alt="Old Mutual Logo" 
              className="h-16 w-auto"
            />
          </div>
          <div className="text-center space-y-2">
            <h1 className="text-2xl md:text-3xl font-display font-bold text-deep-jungle tracking-tight">
              Reset Your Password
            </h1>
            <p className="text-steel text-sm font-medium">
              Enter your new password below
            </p>
          </div>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div 
          className="bg-crystal-white shadow-lg border border-silver-mist mx-4 sm:mx-0 overflow-hidden"
          style={{ borderRadius: '24px' }}
        >
          <div className="px-6 py-8 space-y-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <OmTextField
                  label="New Password"
                  type="password"
                  placeholder="Enter your new password"
                  value={formData.newPassword}
                  onChange={(e) => handleChange('newPassword', e)}
                  error={errors.newPassword}
                  required
                  leadingIcon="lock"
                />

                <OmTextField
                  label="Confirm New Password"
                  type="password"
                  placeholder="Confirm your new password"
                  value={formData.confirmPassword}
                  onChange={(e) => handleChange('confirmPassword', e)}
                  error={errors.confirmPassword}
                  required
                  leadingIcon="lock"
                />
              </div>

              {/* Password requirements hint */}
              <div className="text-xs text-steel space-y-1 bg-silver-mist/30 p-3 rounded-lg">
                <p className="font-semibold text-graphite">Password must contain:</p>
                <ul className="list-disc list-inside space-y-0.5 ml-2">
                  <li>At least 8 characters</li>
                  <li>Uppercase and lowercase letters</li>
                  <li>Numbers</li>
                  <li>Special characters (!@#$%^&*)</li>
                </ul>
              </div>

              <div className="pt-2">
                <OmButton
                  type="submit"
                  disabled={isLoading}
                  className="w-full justify-center text-base font-medium py-3 rounded-2xl"
                >
                  {isLoading ? 'Resetting Password...' : 'Reset Password'}
                </OmButton>
              </div>
            </form>

            {/* Link back to login */}
            <div className="text-center pt-4 border-t border-silver-mist">
              <button
                onClick={() => navigate('/login')}
                className="inline-flex items-center space-x-2 text-sm font-semibold text-fresh-leaf hover:text-forest-fern transition-all duration-300"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                </svg>
                <span>Back to Login</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
